import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Alert,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  AddShoppingCart as AddShoppingCartIcon,
} from '@mui/icons-material';
import api from '../axiosConfig';

const formatMoney = (value) => {
  if (value === null || value === undefined) return '—';
  return `£${Number(value).toFixed(2)}`;
};

const RetailFlagsManager = ({ serviceId }) => {
  const [flags, setFlags] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    setError(null);
    try {
      const [flagsRes, productsRes] = await Promise.all([
        api.get(`/service-retail-flags?service_id=${serviceId}`),
        api.get('/products'),
      ]);
      setFlags(flagsRes.data || []);
      setProducts(productsRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load retail recommendations');
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Don't offer products that are already flagged for this service
  const availableProducts = useMemo(() => {
    const flagged = new Set(flags.map((f) => f.product_id));
    return products.filter((p) => !flagged.has(p.id));
  }, [products, flags]);

  const upsertFlag = (newFlag) => {
    setFlags((prev) => {
      if (prev.find((f) => f.id === newFlag.id)) return prev;
      return [...prev, newFlag];
    });
  };

  const handleAdd = async () => {
    if (!selectedProduct) return;
    setAdding(true);
    setError(null);
    try {
      // Server returns 200 (resurrect) or 201 (new) — both carry the row in body.
      const res = await api.post('/service-retail-flags', {
        service_id: serviceId,
        product_id: selectedProduct.id,
      });
      upsertFlag(res.data);
      setSelectedProduct(null);
    } catch (err) {
      // 409 = live duplicate; server returns existing row, treat as success.
      if (err.response?.status === 409 && err.response?.data?.id) {
        upsertFlag(err.response.data);
        setSelectedProduct(null);
      } else {
        setError(err.response?.data?.error || 'Failed to add product');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (flagId) => {
    setRemovingId(flagId);
    setError(null);
    try {
      await api.delete(`/service-retail-flags/${flagId}`);
      setFlags((prev) => prev.filter((f) => f.id !== flagId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove product');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        Retail recommendations
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Products to suggest at the till when this service is on a booking. If the
        same product is flagged on multiple services in one booking, it only
        appears once at the till. Changes save instantly.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
        <Autocomplete
          fullWidth
          size="small"
          options={availableProducts}
          getOptionLabel={(opt) => opt?.name || ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          value={selectedProduct}
          onChange={(_e, val) => setSelectedProduct(val)}
          loading={loading}
          disabled={loading || adding}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{opt.name}</span>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {formatMoney(opt.retail_price)}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={loading ? 'Loading products…' : 'Search product to recommend…'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          size="medium"
          startIcon={<AddShoppingCartIcon />}
          onClick={handleAdd}
          disabled={!selectedProduct || adding}
          sx={{ flexShrink: 0, height: 40 }}
        >
          Add
        </Button>
      </Box>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : flags.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No retail products flagged for this service yet.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {flags.map((flag) => (
            <Box
              key={flag.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                bgcolor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {flag.product_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMoney(flag.product_retail_price)}
                  {flag.product_barcode ? ` · ${flag.product_barcode}` : ''}
                </Typography>
              </Box>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemove(flag.id)}
                disabled={removingId === flag.id}
                aria-label={`Remove ${flag.product_name} from recommendations`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default RetailFlagsManager;